import React, { useEffect, useMemo, useState } from 'react';
import { X, Trophy, TrendingUp, Eye, Share2 } from 'lucide-react';
import { supabase } from '../supabaseClient';

function normalizeId(value) {
  const normalized = String(value || '').trim();
  return normalized || null;
}

function getTotalVotes(trial) {
  const totalVotes = Number(trial?.total_votes);
  if (Number.isFinite(totalVotes) && totalVotes >= 0) return totalVotes;
  return Number(trial?.votes_a || 0) + Number(trial?.votes_b || 0);
}

function getQueryableCreatorIds({ appUserId, userAuthUserId, authUserId }) {
  return [...new Set([
    normalizeId(appUserId),
    normalizeId(userAuthUserId),
    normalizeId(authUserId),
  ].filter(Boolean))];
}

function getTrialSortValue(trial) {
  const createdAt = trial?.created_at ? new Date(trial.created_at).getTime() : 0;
  return Number.isFinite(createdAt) ? createdAt : 0;
}

function buildCreatorTrialsFilter(ids) {
  return ids
    .flatMap((id) => [`created_by.eq.${id}`, `creator_id.eq.${id}`])
    .join(',');
}

function CreatorAnalytics({ user, trials, authUser, onClose }) {
  const [creatorTrials, setCreatorTrials] = useState([]);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState(null);

  const appUserId = normalizeId(user?.id);
  const userAuthUserId = normalizeId(user?.auth_user_id);
  const authUserId = normalizeId(authUser?.id);
  const creatorIdsKey = useMemo(
    () => getQueryableCreatorIds({ appUserId, userAuthUserId, authUserId }).join('|'),
    [appUserId, userAuthUserId, authUserId]
  );

  useEffect(() => {
    let alive = true;

    async function loadCreatorTrials() {
      const queryableCreatorIds = creatorIdsKey ? creatorIdsKey.split('|').filter(Boolean) : [];
      console.log('[creator_dashboard_identity]', {
        app_user_id: appUserId,
        auth_user_id: authUserId,
        user_auth_user_id: userAuthUserId,
        queryable_creator_ids: queryableCreatorIds,
      });

      if (queryableCreatorIds.length === 0) {
        if (!alive) return;
        setCreatorTrials([]);
        setStatsLoading(false);
        setStatsError('Missing creator identity.');
        return;
      }

      try {
        setStatsLoading(true);
        setStatsError(null);

        const creatorFilter = buildCreatorTrialsFilter(queryableCreatorIds);
        console.log('[creator_dashboard_trials_query]', {
          table: 'trials',
          creator_filter: creatorFilter,
          app_user_id: appUserId,
          auth_user_id: authUserId,
          columns: ['id', 'title', 'side_a', 'side_b', 'status', 'created_at', 'votes_a', 'votes_b', 'total_votes', 'created_by', 'creator_id', 'is_seeded'],
        });

        const { data, error } = await supabase
          .from('trials')
          .select('id, title, side_a, side_b, status, created_at, votes_a, votes_b, total_votes, created_by, creator_id, is_seeded')
          .or(creatorFilter)
          .order('created_at', { ascending: false });

        if (error) throw error;

        const matchedTrials = (Array.isArray(data) ? data : []).filter((trial) => !trial?.is_seeded);

        console.log('[creator_dashboard_votes_query]', {
          source_table: 'trials',
          vote_columns: ['total_votes', 'votes_a', 'votes_b'],
          matched_trial_ids_count: matchedTrials.length,
          matched_trial_ids_preview: matchedTrials.slice(0, 10).map((trial) => trial.id),
          total_votes_computed: matchedTrials.reduce((sum, trial) => sum + getTotalVotes(trial), 0),
        });

        if (!alive) return;
        setCreatorTrials(matchedTrials);
      } catch (err) {
        if (!alive) return;
        console.error('[creator_dashboard_trials_query_failed]', {
          app_user_id: appUserId,
          auth_user_id: authUserId,
          message: err?.message || String(err),
        });
        setCreatorTrials([]);
        setStatsError('Could not load creator stats.');
      } finally {
        if (alive) setStatsLoading(false);
      }
    }

    loadCreatorTrials();
    return () => { alive = false; };
  }, [appUserId, authUserId, userAuthUserId, creatorIdsKey]);

  const myTrials = useMemo(
    () => [...creatorTrials].sort((a, b) => getTrialSortValue(b) - getTrialSortValue(a)),
    [creatorTrials]
  );

  const totalTrials = myTrials.length;
  const totalVotes = myTrials.reduce((sum, trial) => sum + getTotalVotes(trial), 0);
  const avgVotes = totalTrials > 0 ? (totalVotes / totalTrials).toFixed(1) : 0;
  const activeTrials = myTrials.filter((t) => ['active', 'filling'].includes(String(t.status || '').toLowerCase())).length;
  const completedTrials = myTrials.filter((t) =>
    ['ended', 'closed', 'completed', 'verdict_ready', 'sudden_death_failed'].includes(String(t.status || '').toLowerCase())
  ).length;
  const engagedTrials = myTrials.filter((t) => getTotalVotes(t) >= 6).length;
  const engagementRate = totalTrials > 0 ? ((engagedTrials / totalTrials) * 100).toFixed(0) : 0;

  useEffect(() => {
    if (statsLoading) return;
    console.log('[creator_dashboard_stats_result]', {
      app_user_id: appUserId,
      auth_user_id: authUserId,
      matched_trial_ids_count: myTrials.length,
      total_votes_computed: totalVotes,
      final_stats_payload: {
        totalTrials,
        totalVotes,
        avgVotes,
        activeTrials,
        completedTrials,
        engagedTrials,
        engagementRate,
      },
    });
  }, [appUserId, authUserId, myTrials.length, totalVotes, totalTrials, avgVotes, activeTrials, completedTrials, engagedTrials, engagementRate, statsLoading]);

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      padding: '20px'
    }}>
      <div style={{
        background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
        borderRadius: '24px',
        padding: '32px',
        maxWidth: '900px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        border: '2px solid #7c3aed',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px'
        }}>
          <div>
            <h2 style={{
              margin: 0,
              fontSize: '32px',
              fontWeight: '900',
              background: 'linear-gradient(135deg, #7c3aed 0%, #ec4899 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Creator Dashboard
            </h2>
            <p style={{ margin: '4px 0 0 0', color: '#94a3b8', fontSize: '14px' }}>
              Your trial creation stats & analytics
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(148, 163, 184, 0.1)',
              border: '2px solid rgba(148, 163, 184, 0.2)',
              borderRadius: '12px',
              padding: '12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s'
            }}
          >
            <X size={20} color="#94a3b8" />
          </button>
        </div>

        {statsError && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.12)',
            border: '2px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '16px',
            padding: '16px',
            color: '#fecaca',
            marginBottom: '24px',
          }}>
            {statsError}
          </div>
        )}

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px',
          marginBottom: '32px',
          opacity: statsLoading ? 0.65 : 1,
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
            borderRadius: '16px',
            padding: '24px',
            border: '2px solid rgba(124, 58, 237, 0.3)'
          }}>
            <div style={{ fontSize: '14px', color: '#e9d5ff', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Trophy size={16} />
              Trials Created
            </div>
            <div style={{ fontSize: '48px', fontWeight: '900', color: 'white' }}>
              {statsLoading ? '…' : totalTrials}
            </div>
            <div style={{ fontSize: '12px', color: '#c4b5fd', marginTop: '4px' }}>
              {statsLoading ? 'Loading creator trials' : `${activeTrials} active • ${completedTrials} completed`}
            </div>
          </div>

          <div style={{
            background: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
            borderRadius: '16px',
            padding: '24px',
            border: '2px solid rgba(236, 72, 153, 0.3)'
          }}>
            <div style={{ fontSize: '14px', color: '#fce7f3', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <TrendingUp size={16} />
              Total Votes
            </div>
            <div style={{ fontSize: '48px', fontWeight: '900', color: 'white' }}>
              {statsLoading ? '…' : totalVotes}
            </div>
            <div style={{ fontSize: '12px', color: '#fbcfe8', marginTop: '4px' }}>
              Across all your trials
            </div>
          </div>

          <div style={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            borderRadius: '16px',
            padding: '24px',
            border: '2px solid rgba(59, 130, 246, 0.3)'
          }}>
            <div style={{ fontSize: '14px', color: '#dbeafe', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Eye size={16} />
              Avg Votes/Trial
            </div>
            <div style={{ fontSize: '48px', fontWeight: '900', color: 'white' }}>
              {statsLoading ? '…' : avgVotes}
            </div>
            <div style={{ fontSize: '12px', color: '#bfdbfe', marginTop: '4px' }}>
              Average engagement
            </div>
          </div>

          <div style={{
            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            borderRadius: '16px',
            padding: '24px',
            border: '2px solid rgba(245, 158, 11, 0.3)'
          }}>
            <div style={{ fontSize: '14px', color: '#fef3c7', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Share2 size={16} />
              Engagement Rate
            </div>
            <div style={{ fontSize: '48px', fontWeight: '900', color: 'white' }}>
              {statsLoading ? '…' : `${engagementRate}%`}
            </div>
            <div style={{ fontSize: '12px', color: '#fde68a', marginTop: '4px' }}>
              {statsLoading ? 'Loading creator trials' : `${engagedTrials} trials with 6+ votes`}
            </div>
          </div>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, rgba(124, 58, 237, 0.2) 0%, rgba(236, 72, 153, 0.2) 100%)',
          border: '2px solid rgba(124, 58, 237, 0.3)',
          borderRadius: '16px',
          padding: '20px',
          marginBottom: '24px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ fontSize: '32px' }}>
              {totalTrials >= 50 ? '💎' : totalTrials >= 20 ? '🥇' : totalTrials >= 10 ? '🥈' : totalTrials >= 5 ? '🥉' : '🌱'}
            </div>
            <div>
              <div style={{ color: '#e9d5ff', fontSize: '14px', fontWeight: '700' }}>
                Creator Tier
              </div>
              <div style={{ color: 'white', fontSize: '24px', fontWeight: '900' }}>
                {totalTrials >= 50 ? 'Diamond Creator' :
                 totalTrials >= 20 ? 'Gold Creator' :
                 totalTrials >= 10 ? 'Silver Creator' :
                 totalTrials >= 5 ? 'Bronze Creator' : 'Rookie Creator'}
              </div>
            </div>
          </div>
          <div style={{
            marginTop: '12px',
            fontSize: '12px',
            color: '#c4b5fd',
            padding: '12px',
            background: 'rgba(0,0,0,0.2)',
            borderRadius: '8px'
          }}>
            {totalTrials < 5 && `Create ${5 - totalTrials} more trials to reach Bronze Creator!`}
            {totalTrials >= 5 && totalTrials < 10 && `Create ${10 - totalTrials} more trials to reach Silver Creator!`}
            {totalTrials >= 10 && totalTrials < 20 && `Create ${20 - totalTrials} more trials to reach Gold Creator!`}
            {totalTrials >= 20 && totalTrials < 50 && `Create ${50 - totalTrials} more trials to reach Diamond Creator!`}
            {totalTrials >= 50 && `You're at the top tier! Keep creating amazing trials! 🎉`}
          </div>
        </div>

        <div>
          <h3 style={{
            color: 'white',
            fontSize: '20px',
            fontWeight: '900',
            marginBottom: '16px'
          }}>
            Your Trials ({statsLoading ? '…' : totalTrials})
          </h3>

          {!statsLoading && myTrials.length === 0 ? (
            <div style={{
              background: 'rgba(148, 163, 184, 0.1)',
              border: '2px solid rgba(148, 163, 184, 0.2)',
              borderRadius: '12px',
              padding: '32px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '64px', marginBottom: '16px' }}>⚖️</div>
              <div style={{ color: '#94a3b8', fontSize: '16px', fontWeight: '700', marginBottom: '8px' }}>
                No trials created yet
              </div>
              <div style={{ color: '#64748b', fontSize: '14px' }}>
                Create your first trial to start building your creator portfolio!
              </div>
            </div>
          ) : (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              maxHeight: '400px',
              overflow: 'auto',
              padding: '4px',
              opacity: statsLoading ? 0.65 : 1,
            }}>
              {(statsLoading ? [] : myTrials).map((trial) => (
                <div
                  key={trial.id}
                  style={{
                    background: 'rgba(148, 163, 184, 0.1)',
                    border: '2px solid rgba(148, 163, 184, 0.2)',
                    borderRadius: '12px',
                    padding: '16px',
                    transition: 'all 0.2s'
                  }}
                >
                  <div style={{ color: 'white', fontSize: '16px', fontWeight: '800', marginBottom: '8px' }}>
                    {trial.title}
                  </div>
                  <div style={{ color: '#94a3b8', fontSize: '13px', marginBottom: '12px' }}>
                    {trial.side_a} vs {trial.side_b}
                  </div>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                    gap: '8px',
                    fontSize: '12px'
                  }}>
                    <div style={{ color: '#c4b5fd' }}>
                      Status: <strong style={{ color: 'white' }}>{trial.status}</strong>
                    </div>
                    <div style={{ color: '#fbcfe8' }}>
                      Total: <strong style={{ color: 'white' }}>{getTotalVotes(trial)} votes</strong>
                    </div>
                    <div style={{ color: '#bfdbfe' }}>
                      {trial.side_a}: <strong style={{ color: 'white' }}>{Number(trial.votes_a || 0)}</strong>
                    </div>
                    <div style={{ color: '#fde68a' }}>
                      {trial.side_b}: <strong style={{ color: 'white' }}>{Number(trial.votes_b || 0)}</strong>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CreatorAnalytics;
